import { createContext, useEffect, useState } from "react";
import { PanelSettings } from "@/types";
import { IPanelScope, ISearchResult, ITreeItem } from "@/interfaces";
import {
  FoldersService,
  HttpService,
  SearchService,
  TreeService,
} from "@/services";
import { DEFAULT_FOLDER_KEY, GENERIC_ERROR_MESSAGE } from "@/constants";
import { getActiveItemFromUrl, showNotification } from "@/utils";

export interface IFolderStore {
  items: ITreeItem[];
  loading: boolean;
  error: string | null;
  scope: IPanelScope;
  settings: PanelSettings;
  criteria: string;
  selectedItem: any;
  addSubItem: (
    parentId: string,
    name: string,
  ) => Promise<ITreeItem | undefined>;
  setSelectedItem: (node: any) => void;
  setCriteria: (value: string) => void;
  deleteFolder: (id: string) => Promise<void>;
  addFirstLevelFolder: (folderName: string) => Promise<ITreeItem>;
  reloadItems: (newState: ITreeItem[]) => void;
  fetchFolders: () => void;
  setItems: (items: ITreeItem[]) => void;
  handleNodeExpand: (node: ITreeItem) => void;
  handleServerSideSearch: (
    criteria: string,
  ) => Promise<ISearchResult | undefined>;
}

interface IScopeProvider {
  children: React.ReactNode;
  scope: IPanelScope;
  settings: PanelSettings;
}

export const FoldersContext = createContext<IFolderStore | undefined>(
  undefined,
);

export const FoldersProvider = ({
  scope,
  children,
  settings,
}: IScopeProvider) => {
  const [items, setItems] = useState<ITreeItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [criteria, setCriteria] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(
    TreeService.getActiveNode(scope),
  );

  useEffect(() => {
    (async () => {
      await fetchFolders();
    })();
  }, [`${scope.module}${scope.projectId}${scope.userId}`]);

  const reloadItems = async (newState: ITreeItem[]) => {
    setItems([...newState]);
  };

  useEffect(() => {
    if (items) {
      const activeUrlId = getActiveItemFromUrl(scope);
      if (activeUrlId) {
        const activeUrlFilter = SearchService.findFilterItem(
          items,
          activeUrlId,
        );
        setSelectedItem(activeUrlFilter);
      } else {
        const activeNode = TreeService.getActiveNode(scope);
        setSelectedItem(activeNode);
      }
    }
  }, [items]);

  const addSubItem = async (
    parentId: string,
    name: string,
  ): Promise<ITreeItem | undefined> => {
    try {
      const res = await HttpService.post(`/p/${scope.projectId}/folders/`, {
        folder_parent_id: parentId,
        entity_name: scope.module,
        folder: { name },
        tree: "true",
      });

      return FoldersService.transformServerToClient([res.node])[0];
    } catch (e: any) {
      console.error(`FolderContextMenu->handleFolderSave`, e);
      showNotification("error", e?.message || "Failed to save folder");
    }
  };

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const res = await FoldersService.fetchFoldersFromServer(scope);
      setItems(res.items);
    } catch (err) {
      console.error("fetchFolders->Failure fetch folders", err);
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const deleteFolder = async (id: string) => {
    await FoldersService.deleteFolder(scope, id);
    // if current selected item is deleted, clear selected item
    if (selectedItem?.id == id) {
      TreeService.setActiveNode(scope, null);
      setSelectedItem(null);
      window.history.pushState(
        {},
        "",
        `${window.location.origin}${window.location.pathname}?folder=${DEFAULT_FOLDER_KEY}`,
      );
    }
  };

  const addFirstLevelFolder = async (name: string): Promise<ITreeItem> => {
    const res = await FoldersService.addFirstLevelFolder(scope, name);
    return FoldersService.transformServerToClient([res.node])[0];
  };

  const handleNodeExpand = async (node: ITreeItem) => {
    if (node.serverObject.has_children && node.children.length) return;

    node.extra.isLoading = true;
    node.extra.error = null;
    try {
      const children = await FoldersService.getFolderChildren(scope, node.id);
      node.extra.isLoading = false;
      node.extra.hasChildren = !!children.length;
      node.children = [...children];
    } catch (e) {
      console.error(`FolderSearchableTree=>handleNodeExpand`, e);
      node.extra.error = GENERIC_ERROR_MESSAGE;
    } finally {
      node.extra.isLoading = false;
      setItems([...items]);
    }
  };

  const handleServerSideSearch = async (
    criteria: string,
  ): Promise<ISearchResult | undefined> => {
    try {
      setError(null);
      setLoading(true);

      const res = await SearchService.serverSearch(scope, criteria);
      setLoading(false);
      return res;
    } catch (e) {
      console.error(`FolderSearchableTree=>handleServerSideSearch`, e);
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FoldersContext.Provider
      value={{
        settings,
        items,
        loading,
        selectedItem,
        error,
        scope,
        criteria,
        setItems,
        addSubItem,
        addFirstLevelFolder,
        setCriteria,
        setSelectedItem,
        deleteFolder,
        fetchFolders,
        reloadItems,
        handleNodeExpand,
        handleServerSideSearch,
      }}
    >
      {children}
    </FoldersContext.Provider>
  );
};
